import {take, select, put} from 'redux-saga/effects';
import {efServiceAssignmentTypes, efDamageNodeTypes, efServiceCaseTypes} from '@ace-de/eua-entity-types';
import {matchPath} from '@computerrock/formation-router';
import updateServiceAssignment from './updateServiceAssignment';
import updateServiceCase from '../../service-cases/sagas/updateServiceCase';
import isACEPartnerFromDACH from '../isACEPartnerFromDACH';
import * as serviceAssignmentActionTypes from '../serviceAssignmentActionTypes';
import routePaths from '../../routePaths';

const createDamageDescriptionString = (damageNodeSnapshots, damageNodes, translate, translateToEnglish) => {
    let snapshotIds = [...damageNodeSnapshots];
    const description = [];
    damageNodeSnapshots.forEach(nodeId => {
        if (!snapshotIds.includes(nodeId)) return;
        const node = damageNodes[nodeId];
        if (!node) return;
        if (node.nodeType === efDamageNodeTypes.ANSWER) {
            translateToEnglish ? description.push(translate(node.name, {}, 'en-US'))
                : description.push(translate(node.name));
            description.push('\n');
            return;
        }

        if (node.nodeType === efDamageNodeTypes.DECISION) {
            const childrenInSnapshot = node.childNodeIds.filter(childId => damageNodeSnapshots.includes(childId));
            if (childrenInSnapshot.length) {
                translateToEnglish ? description.push(translate(node.name, {}, 'en-US'), ' ')
                    : description.push(translate(node.name), ' ');
                childrenInSnapshot.forEach((childrenId, idx) => {
                    translateToEnglish ? description.push(translate(damageNodes[childrenId].name, {}, 'en-US'))
                        : description.push(translate(damageNodes[childrenId].name));
                    if (idx !== childrenInSnapshot.length - 1) {
                        description.push('***');
                    }
                    snapshotIds = snapshotIds.filter(nodeId => nodeId !== childrenId);
                });
                description.push('\n');
            }
        }
    });
    return description.toString().replaceAll(',', '').replaceAll('***', ', ');
};

/**
 * Select SAV contract partner flow
 */
const selectServiceAssignmentContractPartnerFlow = function* selectServiceAssignmentContractPartnerFlow() {
    const {serviceManager} = yield select(state => state.application);
    const i18nService = serviceManager.loadService('i18nService');
    const {translate} = i18nService;

    while (true) {
        const {payload} = yield take(serviceAssignmentActionTypes.SUBMIT_SERVICE_ASSIGNMENT_CONTRACT_PARTNER_FORM);
        const {serviceAssignmentLineNo, serviceAssignmentData, serviceCaseId} = payload;
        const {acePartner: selectedContractPartner} = serviceAssignmentData;
        const {serviceAssignments} = yield select(state => state.serviceAssignments);
        const {location} = yield select(state => state.router);
        const serviceAssignmentId = `${serviceCaseId}-${serviceAssignmentLineNo}`;
        const serviceAssignment = serviceAssignments[serviceAssignmentId];
        const sapRouteMatch = matchPath(location?.pathname, {
            path: routePaths.SERVICE_ASSIGNMENT_PICKUP,
            exact: true,
        });

        if (!selectedContractPartner) continue;

        const {serviceCases} = yield select(state => state.serviceCases);
        const serviceCase = serviceCases[serviceCaseId];
        const {damageNodeSnapshots} = serviceCase || {};
        const {destination} = serviceAssignment;
        if (damageNodeSnapshots.length > 0) {
            const isDACH = isACEPartnerFromDACH(serviceAssignmentData.acePartner);
            const damageNodes = yield select(state => state.serviceCases.serviceTypeDamageNodes);
            const description = createDamageDescriptionString(damageNodeSnapshots, damageNodes, translate, !isDACH);

            yield* updateServiceCase({
                caller: serviceAssignmentActionTypes.SUBMIT_SERVICE_ASSIGNMENT_CONTRACT_PARTNER_FORM,
                caseType: efServiceCaseTypes.VEHICLE,
                serviceCaseId: serviceAssignment.serviceCaseId,
                serviceCaseData: {
                    damage: {
                        description: description,
                    },
                },
            });
        }

        yield* updateServiceAssignment({
            caller: serviceAssignmentActionTypes.SUBMIT_SERVICE_ASSIGNMENT_CONTRACT_PARTNER_FORM,
            assignmentType: sapRouteMatch
                ? efServiceAssignmentTypes.PICKUP
                : efServiceAssignmentTypes.VEHICLE,
            serviceAssignmentLineNo,
            serviceCaseId,
            serviceAssignmentData: {
                acePartner: {
                    ...selectedContractPartner,
                    ...(selectedContractPartner.businessContactDetails
                        ? {
                            businessContactDetails: {
                                ...selectedContractPartner.businessContactDetails,
                                phoneNo: selectedContractPartner.emergencyContacts?.find(contact => contact.is24h7Emergency)?.phoneNo || '',
                            },
                            contactDetails: null,
                        } : {}
                    ),
                },
                assignmentText: null,
            },
        });
        const isInitialAssignment = serviceAssignment.lineNo === 'FBS1'
            || parseInt(serviceAssignment.lineNo, 10) === 1
            || serviceAssignment.isCloneFromInitial;
        const damageLocation = isInitialAssignment || sapRouteMatch
            ? serviceCase.damage.location
            : serviceAssignment.serviceLocation;

        yield put({
            type: serviceAssignmentActionTypes.INITIATE_CONTRACT_PARTNER_ROUTE_FETCH,
            payload: {
                originLocation: sapRouteMatch ? destination : damageLocation,
                destinationLocation: selectedContractPartner.location,
                serviceAssignmentId,
                contractPartnerId: selectedContractPartner.id,
            },
        });
    }
};

export default selectServiceAssignmentContractPartnerFlow;
